/**
 * HTML renderer component for AG Grid cells that contain pre-rendered HTML
 * content.
 */
export class HtmlRenderer {
  eGui!: HTMLElement;

  init(params: any) {
    const html = this.extractHtmlContent(params);
    this.eGui = this.createGuiElement(html);
    this.addFocusListener();
  }

  getGui() {
    return this.eGui;
  }

  refresh() {
    return false;
  }

  private extractHtmlContent(params: any): string {
    // Early return if no data available
    if (!params.data) {
      return "";
    }

    const displayField = params.colDef?.cellRendererParams?.displayField;

    // Priority order: displayField -> params.value -> field value -> empty
    return displayField && params.data[displayField] !== undefined
      ? params.data[displayField]
      : params.value !== undefined
        ? params.value
        : (params.data[params.colDef?.field] ?? "");
  }

  private createGuiElement(html: string): HTMLElement {
    // If no content, return empty div
    if (!html) {
      return document.createElement("div");
    }

    // Create temporary container to parse HTML
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;

    // Use single root element directly, otherwise wrap in div
    return tempDiv.children.length === 1
      ? (tempDiv.children[0] as HTMLElement)
      : tempDiv;
  }

  /**
   * Add a focus listener to the cell. This will be used to prioritize the
   * z-index of the parent row to ensure floating elements can properly break
   * out of the cell boundaries.
   */
  private addFocusListener(): void {
    if (!this.eGui) return;

    this.eGui.addEventListener("focus", this.handleFocus.bind(this), {
      passive: true,
      capture: true,
    });
  }

  /**
   * Handle the custom cell focus event. If a parent row element is found, it
   * will set the z-index of the row to 1 and enable overflow for all cells in
   * the row while resetting the z-index and overflow of all other rows.
   *
   * @param event - The custom cell focus event.
   */
  private handleFocus(event: Event): void {
    const rowElement = (event.target as HTMLElement)?.closest(
      ".ag-row"
    ) as HTMLElement | null;

    if (rowElement) {
      this.resetAllRows();
      rowElement.style.zIndex = "1";
      this.enableCellOverflow(rowElement);
    }
  }

  /**
   * Reset the z-index and overflow of all rows.
   */
  private resetAllRows(): void {
    document.querySelectorAll(".ag-row").forEach((row) => {
      const rowElement = row as HTMLElement;
      rowElement.style.zIndex = "0";
      this.resetCellOverflow(rowElement);
    });
  }

  /**
   * Enable overflow for all cells in the given row element. AG Grid cells often
   * have overflow: hidden or similar CSS properties that clip floating elements
   * (like dropdown menus) within their boundaries. Prioritizing the z-index
   * alone isn't enough to break out of this clipping context, so we need to
   * also enable overflow for all cells in the row.
   *
   * @param rowElement - The row element to enable overflow for.
   */
  private enableCellOverflow(rowElement: HTMLElement): void {
    rowElement.querySelectorAll(".ag-cell").forEach((cell) => {
      const cellElement = cell as HTMLElement;

      if (!cellElement.dataset.originalOverflow) {
        cellElement.dataset.originalOverflow = cellElement.style.overflow || "";
      }

      cellElement.style.overflow = "visible";
    });
  }

  /**
   * Reset the overflow of all cells in the given row element.
   *
   * @param rowElement - The row element to reset the overflow of.
   */
  private resetCellOverflow(rowElement: HTMLElement): void {
    rowElement.querySelectorAll(".ag-cell").forEach((cell) => {
      const cellElement = cell as HTMLElement;
      if (cellElement.dataset.originalOverflow !== undefined) {
        cellElement.style.overflow = cellElement.dataset.originalOverflow;
        delete cellElement.dataset.originalOverflow;
      } else {
        cellElement.style.overflow = "";
      }
    });
  }
}
